import {
  List,
  IconButton,
  Stack,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import type { AttachmentDto } from "../../../../api/types";
import AttachmentPreview from "../../../../components/AttachmentPreview";
import { DateTimeDisplay } from "../../../../components/DateTimeDisplay";
import { AdminListRow } from "../../../../components/AdminList";
import { formatSize } from "../../../../logic/format-size";

export type AttachmentListViewProps = {
  attachments: AttachmentDto[];
  onManage: (a: AttachmentDto) => void;
  onCreateInvoice: (a: AttachmentDto) => void;
};

export const AttachmentListView = ({
  attachments,
  onManage,
  onCreateInvoice,
}: AttachmentListViewProps) => (
  <Paper variant="outlined">
    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
      {attachments.map((attachment, index) => (
        <AdminListRow
          key={attachment.id}
          divider={index < attachments.length - 1}
          avatar={
            <AttachmentPreview
              attachment={attachment}
              onCreateInvoice={() => onCreateInvoice(attachment)}
            />
          }
          primary={attachment.filename}
          secondary={
            <Stack component="span" spacing={0.25}>
              {/* Type, size and timestamp don't fit one phone-width line. */}
              <Stack
                direction="row"
                component="span"
                useFlexGap
                sx={{ flexWrap: "wrap", columnGap: 2 }}
              >
                <Typography component="span" variant="body2">
                  {attachment.contentType}
                </Typography>
                <Typography component="span" variant="body2">
                  {formatSize(attachment.size)}
                </Typography>
                <DateTimeDisplay date={attachment.createdAt} />
              </Stack>
              {attachment.notes && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontStyle: "italic" }}
                >
                  {attachment.notes}
                </Typography>
              )}
            </Stack>
          }
          actions={
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Create invoice">
                <IconButton
                  aria-label={`Create invoice from ${attachment.filename}`}
                  onClick={() => onCreateInvoice(attachment)}
                >
                  <RequestQuoteIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label={`Manage ${attachment.filename}`}
                onClick={() => onManage(attachment)}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          }
        />
      ))}
    </List>
  </Paper>
);
